/**
 * Search bar component for transaction filtering
 * Extracted from Activity.tsx to improve code organization
 */

import { ReactElement, useRef, useEffect } from "react";
import {
  TextField,
  IconButton,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";

export interface TransactionSearchProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  searchExpanded: boolean;
  onSearchExpandedChange: (expanded: boolean) => void;
}

/**
 * Search bar component with mobile expandable behavior
 */
export default function TransactionSearch({
  searchQuery,
  onSearchChange,
  searchExpanded,
  onSearchExpandedChange,
}: TransactionSearchProps): ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Focus search input when expanded on mobile
  useEffect(() => {
    if (searchExpanded && isMobile) {
      // Use requestAnimationFrame and multiple attempts to ensure keyboard appears
      const focusInput = () => {
        if (searchInputRef.current) {
          const input = searchInputRef.current;
          // Remove readonly/disabled if present
          input.removeAttribute('readonly');
          input.removeAttribute('disabled');
          // Focus the input
          input.focus();
          // Try clicking it as well (some mobile browsers need this)
          input.click();
        }
      };
      
      // Try immediately
      requestAnimationFrame(() => {
        focusInput();
        // Try again after a short delay
        setTimeout(() => {
          focusInput();
        }, 150);
        // One more attempt after longer delay
        setTimeout(() => {
          focusInput();
        }, 300);
      });
    }
  }, [searchExpanded, isMobile]);

  if (isMobile) {
    // Mobile: Expandable search
    return (
      <>
        {searchExpanded ? (
          <TextField
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            size="small"
            inputRef={searchInputRef}
            onClick={(e) => {
              // Ensure input gets focus when TextField is clicked
              if (searchInputRef.current) {
                searchInputRef.current.focus();
              }
            }}
            onFocus={(e) => {
              // Ensure keyboard appears on mobile
              if (isMobile && searchInputRef.current) {
                searchInputRef.current.focus();
              }
            }}
            sx={{ flex: 1 }}
            inputProps={{
              inputMode: "search",
              autoCapitalize: "off",
              autoCorrect: "off",
              readOnly: false,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      onSearchChange("");
                      onSearchExpandedChange(false);
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => onSearchExpandedChange(false)}
                    sx={{ p: 0.5 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        ) : (
          <IconButton
            onClick={() => {
              onSearchExpandedChange(true);
              // Immediately try to focus after state update
              setTimeout(() => {
                if (searchInputRef.current) {
                  const input = searchInputRef.current;
                  input.removeAttribute('readonly');
                  input.removeAttribute('disabled');
                  input.focus();
                  input.click();
                }
              }, 50);
            }}
            sx={{ p: 0.5 }}
            color={searchQuery ? "primary" : "default"}
          >
            <SearchIcon sx={{ fontSize: 22 }} />
          </IconButton>
        )}
      </>
    );
  }

  // Desktop: Always visible search
  return (
    <TextField
      placeholder="Search..."
      value={searchQuery}
      onChange={(e) => onSearchChange(e.target.value)}
      size="small"
      sx={{ minWidth: 150 }}
    />
  );
}
